import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Entity } from 'src/app/core/models/entity.model';
import { Package } from 'src/app/core/models/package.model';
import { Pagination } from 'src/app/core/models/pagination.model';
import { LicenseService } from 'src/app/core/services/license.service';
import { ConfigService } from 'src/app/core/services/config.service';
import { TestsService } from 'src/app/core/services/tests.service';
import { TranslateService } from '@ngx-translate/core';
import * as utils from 'src/app/shared/helpers/utils';
import { hasDeadStandalonePackage } from 'src/app/shared/helpers/standalone-package.helper';
import { User } from 'src/app/core/models/user.model';

@Component({
  selector: 'hbo-no-device-reassign-modal',
  templateUrl: './no-device-reassign-modal.component.html',
  styleUrls: ['./no-device-reassign-modal.component.scss'],
})
export class NoDeviceReassignModalComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() package!: Package | null;
  @Output() confirmed = new EventEmitter<void>();

  targetEntityId: number | null = null;
baseOwner: Entity | null = null;
  filteredEntities: Entity[] = [];
  loading = false;
  error: string | null = null;

  user: User = utils?.localStorageGetItem('user');
  // Whether the chosen destination already had a standalone package that is now dead. Handing it
  // another one would grant the shop a fresh trial period, so only an admin may go ahead — the
  // same rule the general reassign modal applies through its same-group warning.
  destinationHadStandalone = false;
  checkingDestination = false;

  get blockedByDeadStandalone(): boolean {
    return this.destinationHadStandalone && this.user?.roleId !== 1;
  }


  private static readonly ERROR_KEYS: Record<string, string> = {
    ENTITY_HAS_ACTIVE_DEPICT_LICENSE: 'COMMON.REASSIGN.ERRORS.ENTITY_HAS_ACTIVE_DEPICT_LICENSE',
  };

  @ViewChild('modalRoot') modalRoot?: ElementRef<HTMLElement>;

  // Own warning modals, so this flow looks the same as the general reassign one. Their ids are
  // this component's alone: the hosts already own `some-license-inactive` and friends.
  static readonly WARN_BLOCKED = 'no-device-some-license-inactive';
  static readonly WARN_ADMIN = 'no-device-admin-some-license';
  readonly warnBlockedId = NoDeviceReassignModalComponent.WARN_BLOCKED;
  readonly warnAdminId = NoDeviceReassignModalComponent.WARN_ADMIN;

  // The tree below is built from a list narrowed to the base entity's owner, and building it publishes
  // that list into the shared slot every selector's search reads. Put the full one back once this modal
  // is gone, or the page underneath keeps searching over this package's scope. Only on close: while
  // open, the narrowed slot is what keeps this modal's own search inside that scope.
  private onHidden = () => this.jerarquiaService.republishAllEntities();

  constructor(
    private licenseService: LicenseService,
    public configSvc: ConfigService,
    private jerarquiaService: TestsService,
    private translateService: TranslateService,
  ) {}

  ngAfterViewInit(): void {
    this.modalRoot?.nativeElement.addEventListener('hidden.bs.modal', this.onHidden);
  }

  ngOnDestroy(): void {
    this.modalRoot?.nativeElement.removeEventListener('hidden.bs.modal', this.onHidden);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['package'] && this.package) {
      this.targetEntityId = null;
      this.error = null;
      this.buildFilteredEntities(this.package.assigned);
    }
  }

  private buildFilteredEntities(baseEntity: any): void {
    if (!baseEntity) return;
    const allEntities: Entity[] = this.configSvc.currentConfigValue.childEntities || [];
    const entityMap = new Map<number, Entity>(allEntities.map((e: Entity) => [e.id, e]));
    const userEntity = this.configSvc.currentConfigValue.userEntity;
    if (userEntity) entityMap.set(userEntity.id, userEntity);

    // Look up full entity from childEntities to get parentEntityId
    const baseEntityFull: Entity = entityMap.get(baseEntity.id) ?? baseEntity;

    // The base entity's direct parent owns it — a distributor or a head office.
    // Targets stay inside that owner's subtree, so a package never crosses to a sibling structure.
    this.baseOwner = baseEntityFull.parentEntityId ? entityMap.get(baseEntityFull.parentEntityId) ?? null : null;

    if (!this.baseOwner) { this.filteredEntities = []; return; }
    const ownerId = this.baseOwner.id;

    // All non-base entities under the owner (head offices + opticals)
    const isUnder = (e: Entity): boolean => {
      let node: Entity | undefined = e;
      while (node) {
        if (node.id === ownerId) return true;
        if (!node.parentEntityId) break;
        node = entityMap.get(node.parentEntityId);
      }
      return false;
    };

    this.filteredEntities = allEntities.filter((e: Entity) => !e.isBase && isUnder(e));
  }

  onBvsCallback(entityId: number): void {
    // The selector also emits the string 'change' on every search keystroke, not just an id.
    // Acting on that would fire one request per character — and with a nonsense entity.
    const id = Number(entityId);
    if (!Number.isInteger(id) || id <= 0) return;
    if (id === this.targetEntityId) return;

    this.targetEntityId = id;
    this.destinationHadStandalone = false;
    this.error = null;

    // The destination's own packages: the backend only rejects a LIVE standalone, so a dead one
    // has to be spotted here or the shop quietly gets a second trial.
    this.checkingDestination = true;
    this.licenseService.availableAllPackages({
      entities: `[${this.targetEntityId}]`, entityId: this.targetEntityId, perPage: -1
    }).subscribe({
      next: (res: Pagination<Package>) => {
        this.checkingDestination = false;
        this.destinationHadStandalone = hasDeadStandalonePackage(
          this.configSvc.currentConfigValue.packageTypes, res?.data, this.package?.id);
        if (this.destinationHadStandalone) this.raiseWarning();
      },
      error: () => { this.checkingDestination = false; }
    });
  }

  confirm(): void {
    if (!this.package || !this.targetEntityId || this.blockedByDeadStandalone) return;
    this.loading = true;
    this.error = null;
    this.licenseService.updatePackage({
      id: this.package.id,
      reassignNoDevice: true,
      assignedEntityId: this.targetEntityId,
    } as any).subscribe({
      next: () => {
        this.loading = false;
        utils.forceModal('#noDeviceReassignModal', true);
        this.confirmed.emit();
      },
      error: (err) => {
        this.loading = false;
        this.error = this.resolveErrorMessage(err);
      }
    });
  }

  // Swap this modal for the warning, the same way the general reassign flow does it. Reopening
  // afterwards is safe here: nothing in this component resets its state on hide.
  //
  // Open first, dismiss second, always. forceModal reuses one shared trigger element and only
  // sets its data-bs-target when it CREATES it, and its dismiss branch waits 500ms before
  // removing it — so a dismiss followed by an open makes the open reuse the stale target.
  private raiseWarning(): void {
    utils.forceModal(`#confirmationModal-${this.activeWarningId}`, false);
    utils.forceModal('#noDeviceReassignModal', true);
  }

  // "Understood" on either warning brings the assignment modal back, with the destination kept.
  onWarningAcknowledged(): void {
    utils.forceModal('#noDeviceReassignModal', false);
    utils.forceModal(`#confirmationModal-${this.activeWarningId}`, true);
  }

  private get activeWarningId(): string {
    return this.blockedByDeadStandalone ? this.warnBlockedId : this.warnAdminId;
  }

  private resolveErrorMessage(err: unknown): string {
    const body = (err as { error?: { code?: string; error?: string; message?: string } })?.error;
    const key = body?.code ? NoDeviceReassignModalComponent.ERROR_KEYS[body.code] : undefined;

    if (key) return this.translateService.instant(key);

    return body?.error
      || body?.message
      || this.translateService.instant('COMMON.REASSIGN.ERRORS.GENERIC');
  }
}
